/**
 * My Account Page
 * 
 * Allows users to update their profile name, email, and password.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  User,
  Mail,
  Lock,
  Loader2,
  ArrowLeft,
  Shield,
  CheckCircle,
  Building
} from 'lucide-react';

export default function MyAccountPage() {
  const navigate = useNavigate();
  const { user, organisations = [] } = useOutletContext() || {};

  // Account form state
  const [accountData, setAccountData] = useState({
    name: '',
    email: '',
    newPassword: '',
    confirmPassword: '',
    defaultOrgId: 'none'
  });
  const [accountLoading, setAccountLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'password'
  const [loading, setLoading] = useState(true);

  // Initialize data from context
  useEffect(() => {
    if (user) {
      setAccountData(prev => ({
        ...prev,
        name: user.user_metadata?.full_name || '',
        email: user.email || '',
        defaultOrgId: user.user_metadata?.default_organisation_id || 'none'
      }));
      setLoading(false);
    } else {
      // If no user in context, fallback or wait (but MemberLayout should handle this)
    }
  }, [user]);

  // Update user profile (name)
  const handleUpdateProfile = async () => {
    if (!accountData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setAccountLoading(true);
    try {
      // Update in Supabase Auth
      const { error: authError } = await supabase.auth.updateUser({
        data: { full_name: accountData.name.trim() }
      });

      if (authError) throw authError;

      // Also update in users table
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { data: userData } = await supabase
          .from('users')
          .select('id')
          .eq('supabase_user_id', currentUser.id)
          .single();

        if (userData) {
          await supabase
            .from('users')
            .update({ name: accountData.name.trim() })
            .eq('id', userData.id);
        }
      }

      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setAccountLoading(false);
    }
  };

  // Update user email
  const handleUpdateEmail = async () => {
    if (!accountData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (accountData.email === user?.email) {
      toast.info('Email is the same as current');
      return;
    }

    setAccountLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        email: accountData.email.trim()
      });

      if (error) throw error;

      toast.success('Verification email sent! Please check your inbox to confirm the new email.');
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error(error.message || 'Failed to update email');
    } finally {
      setAccountLoading(false);
    }
  };

  // Update default organisation
  const handleUpdateDefaultOrg = async (value) => {
    setAccountData(prev => ({ ...prev, defaultOrgId: value }));
    setAccountLoading(true);
    try {
      const orgIdToSave = value === 'none' ? null : value;
      const { error } = await supabase.auth.updateUser({
        data: { default_organisation_id: orgIdToSave }
      });

      if (error) throw error;
      toast.success('Default organisation updated successfully!');
    } catch (error) {
      console.error('Error updating default organisation:', error);
      toast.error(error.message || 'Failed to update default organisation');
    } finally {
      setAccountLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (!accountData.newPassword) {
      toast.error('New password is required');
      return;
    }

    if (accountData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // Check for uppercase or special character
    const hasUppercase = /[A-Z]/.test(accountData.newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(accountData.newPassword);
    if (!hasUppercase && !hasSpecialChar) {
      toast.error('Password must contain at least 1 uppercase letter or special character');
      return;
    }

    if (accountData.newPassword !== accountData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setAccountLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: accountData.newPassword
      });

      if (error) throw error;

      toast.success('Password changed successfully!');
      setAccountData(prev => ({ ...prev, newPassword: '', confirmPassword: '' }));
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      setAccountLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#003B5C] mx-auto mb-4" />
          <p className="text-slate-600">Loading account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <User className="w-7 h-7 mr-3 text-[#74A57F]" />
              My Account
            </h1>
            <p className="text-slate-600 mt-1">
              Manage your profile and security settings
            </p>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden" data-testid="my-account-page">
        {/* Tabs */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${activeTab === 'profile'
                ? 'bg-white text-[#003B5C] shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
                }`}
              data-testid="profile-tab"
            >
              <User className="w-4 h-4 mr-2" />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${activeTab === 'password'
                ? 'bg-white text-[#003B5C] shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
                }`}
              data-testid="password-tab"
            >
              <Shield className="w-4 h-4 mr-2" />
              Password
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'profile' ? (
            <div className="space-y-6">
              {/* Name Section */}
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 font-medium">Full Name</Label>
                  <Input
                    value={accountData.name}
                    onChange={(e) => setAccountData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your full name"
                    className="mt-1.5"
                    data-testid="name-input"
                  />
                </div>

                <Button
                  onClick={handleUpdateProfile}
                  disabled={accountLoading}
                  className="w-full bg-[#74A57F] hover:bg-[#5F8575] text-white"
                  data-testid="update-name-btn"
                >
                  {accountLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Update Name
                </Button>
              </div>

              {/* Email Section */}
              <div className="border-t border-slate-200 pt-6 space-y-4">
                <div>
                  <Label className="text-slate-700 font-medium">Email Address</Label>
                  <Input
                    type="email"
                    value={accountData.email}
                    onChange={(e) => setAccountData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="mt-1.5"
                    data-testid="email-input"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">
                    Changing your email requires verification via the new address
                  </p>
                </div>

                <Button
                  onClick={handleUpdateEmail}
                  disabled={accountLoading || accountData.email === user?.email}
                  variant="outline"
                  className="w-full"
                  data-testid="update-email-btn"
                >
                  {accountLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Update Email
                </Button>
              </div>

              {/* Default Organisation Section */}
              <div className="border-t border-slate-200 pt-6 space-y-4">
                <div>
                  <Label className="text-slate-700 font-medium flex items-center">
                    <Building className="w-4 h-4 mr-2" />
                    Default Organisation
                  </Label>
                  <p className="text-xs text-slate-500 mt-1 mb-3">
                    Choose which organisation loads automatically when you first log in.
                  </p>
                  <Select
                    value={accountData.defaultOrgId}
                    onValueChange={handleUpdateDefaultOrg}
                    disabled={accountLoading}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select default organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Use last active)</SelectItem>
                      {organisations.map(org => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Password Requirements</p>
                    <ul className="text-xs text-blue-600 mt-1 space-y-0.5">
                      <li>• Minimum 8 characters</li>
                      <li>• At least 1 uppercase letter OR special character</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 font-medium">New Password</Label>
                  <Input
                    type="password"
                    value={accountData.newPassword}
                    onChange={(e) => setAccountData(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Enter new password"
                    className="mt-1.5"
                    data-testid="new-password-input"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 font-medium">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={accountData.confirmPassword}
                    onChange={(e) => setAccountData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm new password"
                    className="mt-1.5"
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  onClick={handleChangePassword}
                  disabled={accountLoading || !accountData.newPassword || !accountData.confirmPassword}
                  className="w-full bg-[#003B5C] hover:bg-[#002A45] text-white"
                  data-testid="change-password-btn"
                >
                  {accountLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  Change Password
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
